import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { apiFetch } from "../../context/AppContext";

function scoreColor(score) {
  if (score >= 75) return "#10b981";
  if (score >= 50) return "#f59e0b";
  return "#ef4444";
}

function questionPct(q) {
  if (!q?.applicable || !q.max_answer_score) return null;
  return Math.round((q.answer_score / q.max_answer_score) * 100);
}

function safeJsonBody(formData) {
  const seen = new WeakSet();
  return JSON.stringify(
    { form_data: formData },
    (_key, value) => {
      if (typeof value === "function" || typeof value === "symbol") return undefined;
      if (value && typeof value === "object") {
        if (seen.has(value)) return undefined;
        seen.add(value);
      }
      return value;
    },
  );
}

function formDataSignature(formData) {
  try {
    return safeJsonBody(formData);
  } catch {
    return "";
  }
}

/**
 * Live weighted score for PIA / ROPA forms.
 * variant="sidebar" — compact card for narrow sidebars (default in forms).
 */
export default function FormScorePanel({
  scorePath,
  formData,
  levelLabel = "Level",
  accent = "#6366f1",
  variant = "sidebar",
}) {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const timer = useRef(null);
  const formSignature = useMemo(
    () => formDataSignature(formData),
    [formData],
  );

  const fetchScore = useCallback(async () => {
    if (!scorePath || !formSignature) return;
    setLoading(true);
    try {
      const res = await apiFetch(scorePath, {
        method: "POST",
        body: formSignature,
      });
      if (res.ok) {
        const data = await res.json();
        setScore(data);
      }
    } catch {
      /* ignore transient score errors */
    } finally {
      setLoading(false);
    }
  }, [scorePath, formSignature]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(fetchScore, 400);
    return () => clearTimeout(timer.current);
  }, [fetchScore]);

  if (!score && !loading) return null;

  const pct = score?.score ?? 0;
  const color = score ? scoreColor(pct) : accent;
  const sections = (score?.sections || []).filter((s) => s.applicable_weight > 0);

  if (variant === "sidebar") {
    return (
      <div
        style={{
          background: "#fafbff",
          border: `1px solid ${color}22`,
          borderRadius: 10,
          padding: "8px 10px",
          marginBottom: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 6,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: "#64748b",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                lineHeight: 1.2,
              }}
            >
              Weighted score
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                marginTop: 2,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: 18,
                  fontWeight: 800,
                  color,
                  lineHeight: 1,
                }}
              >
                {loading && !score ? "…" : `${pct}%`}
              </span>
              {score?.level_label && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color,
                    padding: "1px 6px",
                    borderRadius: 999,
                    background: `${color}10`,
                    border: `1px solid ${color}30`,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 96,
                  }}
                  title={`${levelLabel}: ${score.level_label}`}
                >
                  {score.level_label}
                </span>
              )}
            </div>
          </div>
          {sections.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              title="Section breakdown"
              style={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: "3px 5px",
                fontSize: 9,
                fontWeight: 600,
                color: "#64748b",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
              {sections.length}
            </button>
          )}
        </div>

        <div
          style={{
            marginTop: 6,
            height: 3,
            borderRadius: 99,
            background: "#e2e8f0",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: color,
              borderRadius: 99,
              transition: "width 0.4s ease",
            }}
          />
        </div>

        {score?.scorable_questions != null && (
          <div style={{ marginTop: 4, fontSize: 9, color: "#94a3b8" }}>
            {score.answered_questions}/{score.scorable_questions} questions scored
          </div>
        )}

        {expanded && sections.length > 0 && (
          <div
            style={{
              marginTop: 8,
              paddingTop: 8,
              borderTop: "1px solid #f1f5f9",
              maxHeight: 88,
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(99,102,241,0.35) transparent",
              display: "flex",
              flexDirection: "column",
              gap: 5,
            }}
          >
            {sections.map((sec) => (
              <div
                key={sec.section_id}
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <span
                  style={{
                    flex: 1,
                    fontSize: 9,
                    color: "#475569",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={sec.title || sec.section_id}
                >
                  {sec.title || sec.section_id}
                </span>
                <div
                  style={{
                    width: 40,
                    height: 3,
                    borderRadius: 99,
                    background: "#f1f5f9",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: `${sec.score}%`,
                      height: "100%",
                      background: scoreColor(sec.score),
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: scoreColor(sec.score),
                    width: 26,
                    textAlign: "right",
                    flexShrink: 0,
                  }}
                >
                  {sec.score}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* full variant — dashboards / detail views */
  return (
    <div
      style={{
        borderRadius: 14,
        border: "1px solid rgba(99,102,241,0.12)",
        background: "#ffffff",
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}
    >
      <div style={{ padding: "12px 14px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: "#64748b",
                letterSpacing: "0.06em",
              }}
            >
              WEIGHTED SCORE
            </div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 800,
                color,
                lineHeight: 1.1,
              }}
            >
              {loading ? "…" : `${pct}%`}
            </div>
          </div>
          {score?.level_label && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color,
                padding: "4px 10px",
                borderRadius: 999,
                background: `${color}18`,
                border: `1px solid ${color}40`,
              }}
            >
              {levelLabel}: {score.level_label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function QuestionScoreBadge({ questionScore }) {
  if (!questionScore?.applicable) return null;
  const pct =
    questionScore.max_answer_score > 0
      ? Math.round(
          (questionScore.answer_score / questionScore.max_answer_score) * 100,
        )
      : 0;
  const color = scoreColor(pct);
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color,
        padding: "2px 6px",
        borderRadius: 6,
        background: `${color}14`,
        border: `1px solid ${color}30`,
      }}
    >
      {pct}%
    </span>
  );
}
